<template>
    <Teleport to="#modals-box">
        <div class="modal-container"
            @click="closeOutside"
            :class="{
                'full-size': props.full,
                transparent: props.transparent,
                popover: props.popover
            }"
        >
            <div class="modal"
                ref="modalRef"
                :class="props.class"
                v-if="!data.isMobile || !props.mobileMode"
                @click.stop=""
                :style="props.popover ? popoverStyle : null"
            >
                <div
                    class="header"
                    v-if="slots.title"
                    @pointerdown.passive="onHeaderPointerDown"
                >
                    <h3>
                        <slot name="title"></slot>
                    </h3>

                    <IconButton
                        v-if="props.canCancel"
                        icon="close"
                        class="tertiary"
                        :class="{
                            invert: props.popover,
                            'size-s': !props.popover,
                            'size-l': props.popover
                        }"
                        @click="close"
                    />
                </div>

                <div class="content">
                    <slot name="content"></slot>
                </div>

                <div class="loader" v-if="props.isLoading">
                    <div>
                        <Loader :inside="true" class="tertiary" />
                    </div>
                </div>

                <div class="footer" v-if="slots.footer">
                    <slot name="footer"></slot>
                </div>
            </div>

            <ModalBox v-else
                @close="close"
                @onSwipeBottom="close"
                :draggable="props.draggable"
                :size="props.full ? 'size-xl' : 'size-l'"
                :secondary="props.secondary"
            >
                <div class="header" v-if="slots.title">
                    <h3>
                        <slot name="title"></slot>
                    </h3>
                </div>
                <div class="content">
                    <slot name="content"></slot>
                </div>

                <div class="loader" v-if="props.isLoading">
                    <div>
                        <Loader :inside="true" class="tertiary" />
                    </div>
                </div>
            </ModalBox>
        </div>
    </Teleport>
</template>

<script setup>
import { useSlots, reactive, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import Button from '🔗/components/ui/Button.vue';
import IconButton from '🔗/components/ui/IconButton.vue';
import Loader from '🔗/components/ui/Loader.vue';
import ModalBox from '🔗/components/ui/ModalBox.vue';
import { isMobile } from '🔗/utils/utils.js';

import stores from '@/stores';
const modals = stores.modals();

const emit = defineEmits(['close']);
const slots = useSlots();

const props = defineProps({
    full: { type: Boolean, default: false },
    canCancel: { type: Boolean, default: true },
    name: { type: String, required: true },
    isLoading: { type: Boolean, default: false },
    closeOutside: { type: Boolean, default: true },
    mobileMode: { type: Boolean, default: false },
    popover: { type: Boolean, default: false },
    transparent: { type: Boolean, default: false },
    secondary: { type: Boolean, default: false },
    draggable: { type: Boolean, default: true },
    class: { type: [String, null], default: '' }
});

const data = reactive({
    isMobile: isMobile()
});

const modalRef = ref(null);

// ----- позиция / сохранение -----
const DEFAULT_TOP = 80;
const DEFAULT_RIGHT = 24;
const MIN_HEIGHT = 120; // минимальная высота поповера при перетаскивании вниз

const pos = reactive({
    top: DEFAULT_TOP,
    left: null,   // число px либо null
    right: DEFAULT_RIGHT // число px либо null
});

const storageKey = `modal-popover-${props.name}`;

const loadSavedPos = () => {
    try {
        // if (typeof parsed.top === 'number') {
        //     pos.top = parsed.top;
        // }
        
        // if (typeof parsed.left === 'number') {
        //     pos.left = parsed.left;
        //     pos.right = null;
        // } else if (typeof parsed.right === 'number') {
        //     pos.right = parsed.right;
        //     pos.left = null;
        // }

        const raw = localStorage.getItem(storageKey);

        if (!raw) {
            return;
        }

        const parsed = JSON.parse(raw);

        if (typeof parsed.top === 'number') {
            pos.top = Math.round(parsed.top * window.innerHeight);
        }
        if (typeof parsed.left === 'number') {
            pos.left = Math.round(parsed.left * window.innerWidth);
            pos.right = null;
        } else if (typeof parsed.right === 'number') {
            pos.right = Math.round(parsed.right * window.innerWidth);
            pos.left = null;
        }
    } catch (e) {
        // ignore parse errors
    }
}

const savePos = () => {
    try {
        // const toSave = {
        //     top: Math.round(pos.top),
        //     left: pos.left !== null ? Math.round(pos.left) : null,
        //     right: pos.right !== null ? Math.round(pos.right) : null
        // };

        // localStorage.setItem(storageKey, JSON.stringify(toSave));
        const toSave = {
            top: pos.top / window.innerHeight,
            left: pos.left !== null ? pos.left / window.innerWidth : null,
            right: pos.right !== null ? pos.right / window.innerWidth : null
        };
        localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
        // ignore
    }
}

const popoverStyle = computed(() => {
    if (!props.popover) {
        return null;
    }

    const style = {
        position: 'fixed',
        top: `${Math.round(pos.top)}px`,
        bottom: '24px',
        width: '360px',
        transform: 'none'
    };

    if (pos.left !== null) style.left = `${Math.round(pos.left)}px`;
    else style.right = `${Math.round(pos.right)}px`;
    return style;
});

// ----- перетаскивание -----
let dragging = false;
let pointerId = null;
let startX = 0;
let startY = 0;
let startLeft = 0;
let startRight = 0;
let startTop = 0;
let modalRectCache = null;

const clampTop = (t) => {
    const vh = window.innerHeight;
    const maxTop = Math.max(0, vh - 24 - MIN_HEIGHT); // не позволяем сделать высоту меньше MIN_HEIGHT
    return Math.min(Math.max(0, t), maxTop);
}

const onHeaderPointerDown = (e) => {
    // перетаскивание только для popover
    if (!props.popover) {
        return;
    }

    // игнорируем некнопочные pointer'ы
    if (e.button && e.button !== 0) {
        return;
    }

    // начальные значения
    dragging = true;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;

    const el = modalRef.value;
    if (!el) {
        return;
    }

    modalRectCache = el.getBoundingClientRect();
    startLeft = modalRectCache.left;
    startRight = window.innerWidth - modalRectCache.right;
    startTop = modalRectCache.top;

    // если у нас был инлайн right — мы будем сохранять left после первого движения (конвертация)
    // захватываем указатель
    try { e.target.setPointerCapture(pointerId); } catch (err) {}

    window.addEventListener('pointermove', onGlobalPointerMove);
    window.addEventListener('pointerup', onGlobalPointerUp);
    window.addEventListener('pointercancel', onGlobalPointerUp);
}

const onGlobalPointerMove = (e) => {
    if (!dragging || e.pointerId !== pointerId) {
        return;
    }

    if (!modalRectCache) {
        modalRectCache = modalRef.value?.getBoundingClientRect();
    }

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    // горизонталь — устанавливаем left (конвертируем из right в left при первом движении)
    const newLeft = startLeft + dx;

    // clamp left в пределах margin 12..(viewport - width - 12)
    const margin = 12;
    const maxLeft = Math.max(margin, window.innerWidth - modalRectCache.width - margin);
    const clampedLeft = Math.min(Math.max(margin, newLeft), maxLeft);

    pos.left = Math.round(clampedLeft);
    pos.right = null;

    // вертикаль — меняем top, но не позволяем опустить так, чтобы высота была меньше MIN_HEIGHT
    const newTop = startTop + dy;
    pos.top = clampTop(newTop);
}

const onGlobalPointerUp = (e) => {
    if (!dragging || e.pointerId !== pointerId) {
        return;
    }

    dragging = false;
    pointerId = null;
    modalRectCache = null;

    window.removeEventListener('pointermove', onGlobalPointerMove);
    window.removeEventListener('pointerup', onGlobalPointerUp);
    window.removeEventListener('pointercancel', onGlobalPointerUp);

    // 
    savePos();
}

const onWindowResize = () => {
    // при ресайзе проверяем вертикальную позицию: top не должен быть больше допустимого
    pos.top = clampTop(pos.top);

    // горизонталь — не даём уйти за правую грань
    if (modalRef.value) {
        const rect = modalRef.value.getBoundingClientRect();
        const margin = 12;
        const maxLeft = Math.max(margin, window.innerWidth - rect.width - margin);

        if (pos.left !== null) {
            pos.left = Math.min(Math.max(margin, pos.left), maxLeft);
        } else if (pos.right !== null) {
            const maxRight = Math.max(margin, window.innerWidth - rect.width - margin);
            pos.right = Math.min(Math.max(margin, pos.right), maxRight);
        }
    }

    savePos();
}

onMounted(() => {
    if (props.popover) {
        loadSavedPos();
    }

    window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('pointermove', onGlobalPointerMove);
    window.removeEventListener('pointerup', onGlobalPointerUp);
    window.removeEventListener('pointercancel', onGlobalPointerUp);
});

const close = () => {
    emit('close');
    modals.close(props.name);
};

const closeOutside = () => {
    if (props.closeOutside) {
        emit('close');
        modals.close(props.name);
    }
};
</script>


<style lang="scss" scoped>
.modal-container {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    height: 100%;
    // max-height: var(--tg-viewport-height);
    max-height: var(--max-viewport-height, var(--tg-viewport-height, 100vh));
    
    background: var(--white-80);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    z-index: 999;
    padding: 20px;
    animation: modal-bg ease-out .2s;

    .modal {
        width: 100%;
        background-color: var(--white-500);
        border-radius: 10px;
        padding: 20px 0 0;
        position: relative;
        display: flex;
        flex-direction: column;
        position: relative;
        animation: modal-in ease-out .1s;
        box-shadow: 0px 0px 8px -4px var(--dark-500);
        gap: 20px;

        .loader {
            background-color: var(--white-500);
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            border-radius: 10px;
            & > div {
                margin: auto;
                width: 120px;
                height: 50px;
                position: relative;
                overflow: hidden;
            }
        }

        .footer {
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
            min-height: 38px;
            display: flex;
            align-items: center;
            width: 100%;
            z-index: 1;
            padding: 0;
        }

        .header {
            border-top-right-radius: 20px;
            border-top-left-radius: 20px;
            min-height: 48px;
            display: flex;
            align-items: center;
            width: 100%;
            z-index: 1;
            padding: 0;

            h3 {
                margin: 0 auto;
                padding: 0 40px;
                font: var(--font-h5);
                font-weight: bold;
                color: var(--dark-500);
                text-align: center;
                display: flex;
                flex-direction: column;
                gap: 16px;
            }

            button {
                position: absolute;
                right: 10px;
                top: 10px;
                // top: 50%;
                // transform: translateY(-50%);
            }
        }

        .content {
            padding: 0 20px 20px;
            overflow: auto;
            flex: 1 1;
            display: flex;
            flex-direction: column;
            gap: 20px;
            z-index: 1;
            @include scrollbar();
        }
        &.with-sticky-buttons {
            .content {
                padding-bottom: 0;
            }
        }
    }

    &.transparent {
        animation: modal-transparent ease-out .2s !important;
        .modal {
            background: transparent;
            background-color: transparent;
        }
    }

    &.full-size {
        background: transparent;
        background-color: transparent;
        padding: 0;
        animation: modal-full-bg ease-out .2s;
        .modal {
            height: 100%;
            border-radius: 0;
            animation: none;
            box-shadow: none;
        }

        &.light {
            .modal {
                .content {
                    padding: 0;
                }

                &:before {
                    content: none;
                }
            }
        }
    }

    &.popover {
        background: transparent;
        background-color: transparent;
        padding: 0;
        pointer-events: none;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        .modal {
            pointer-events: auto;
            width: 360px;
            left: auto;
            right: 24px;
            transform: none;
            position: fixed;
            top: 80px;
            bottom: 24px;
            background: var(--dark-90);
            backdrop-filter: blur(6px);
            -webkit-backdrop-filter: blur(6px);
            padding-top: 0;
            gap: 0;
            overflow: hidden;

            .header {
                background: var(--dark-500);
                padding-top: 24px;
                padding-bottom: 24px;
                cursor: grab;
                h3 {
                    gap: 8px;
                    color: var(--white-500);
                    text-align: start;
                    margin: 0;
                    padding: 0 56px 0 24px;
                    width: 100%;
                }
                button {
                    right: 7px;
                    top: 7px;
                }
            }

            .footer {
                background: var(--dark-500);
                padding: 0 12px 8px;
                // padding-top: 24px;
                // padding-bottom: 24px;
                // cursor: grab;
            }

            .content {
                padding: 0;
                position: relative;
                gap: 0;

                &:before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    min-height: 20px;
                    background: linear-gradient(0deg, transparent 0%, var(--dark-500) 100%);
                    display: block;
                    z-index: 1;
                }

                &:after {
                    content: "";
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    min-height: 20px;
                    background: linear-gradient(0deg, var(--dark-500) 0%, transparent 100%);
                    display: block;
                    z-index: 1;
                }
            }

            .loader {
                background: var(--dark-500);
            }
        }
    }

    &:empty {
        display: none;
    }
}

@keyframes modal-transparent {
    0% {
        backdrop-filter: blur(0px);
        -webkit-backdrop-filter: blur(0px);
    }
    100% {
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
    }
}

@keyframes modal-full-bg {
    0% {
        background: var(--white-5);
        backdrop-filter: blur(0px);
        -webkit-backdrop-filter: blur(0px);
    }
    100% {
        background: var(--white-80);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
    }
}

@keyframes modal-full-bg {
    0% {
        background: rgb(34 34 34 / 1%);
    }
    100% {
        background: rgb(34 34 34 / 72%);
    }
}

@keyframes modal-in {
    0% {
        transform: scale(.8);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes modal-bg-2 {
    0% {
        background-position-x: 0%;
        background-position-y: 0%;
        background-size: 82%;
    }
    50% { 
        background-position-x: 50%;
        background-size: 152%;
    }
    100% { 
        background-position-x: 100%;
        background-position-y: 100%;
        background-size: 82%;
    }
}
</style>
